import * as v from 'valibot';
export const TAG_DOC = 0x01;
export const TAG_PRESENCE = 0x02;
/**
 * Cloudflare closes a Durable Object WebSocket with code 1009 if either side
 * emits a message above this size (raised from 1 MiB in Oct 2025). A single
 * CRDT delta or presence update is never remotely close to this in practice
 * — the only realistic way to hit it is a full-history export to a client
 * that has never synced. Guarding at encode time turns that into a caught,
 * observable error (existing `onError`/`obs.error` paths) instead of a raw
 * socket close with no diagnostic.
 *
 * ponytail: rejects rather than chunks. Bounded chunking would need a
 * reassembly protocol on the decode side (buffer parts, concat, then treat
 * as one frame) — real work, not worth it until an oversized frame actually
 * fires in production. Upgrade path: multi-part `TAG_DOC` frames with a
 * part-index/total header, reassembled before `doc.import`.
 */
export const MAX_FRAME_BYTES = 32 * 1024 * 1024;
/**
 * Encodes a tagged binary frame as `tag || payload`.
 *
 * @param tag - Binary frame discriminator written to the first byte.
 * @param payload - Bytes written after the discriminator.
 * @returns A newly encoded tagged frame.
 * @throws If the encoded frame would exceed {@link MAX_FRAME_BYTES}.
 */
export function encodeBinaryFrame(tag, payload) {
    const size = payload.length + 1;
    if (size > MAX_FRAME_BYTES) {
        throw new Error(`binary frame of ${size} bytes exceeds the ${MAX_FRAME_BYTES}-byte WebSocket message limit`);
    }
    const frame = new Uint8Array(size);
    frame[0] = tag;
    frame.set(payload, 1);
    return frame;
}
/**
 * Decodes a tagged binary frame produced by {@link encodeBinaryFrame}.
 *
 * @param frame - Tagged frame to decode.
 * @returns The decoded tag and payload view.
 * @throws If the frame is empty or carries an unknown tag.
 */
export function decodeBinaryFrame(frame) {
    if (frame.length === 0)
        throw new Error('binary frame is empty');
    const tag = frame[0];
    if (tag !== TAG_DOC && tag !== TAG_PRESENCE) {
        throw new Error(`unknown binary tag: ${tag}`);
    }
    return { tag, payload: frame.subarray(1) };
}
const commentBody = v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(4000));
const commentMentions = v.optional(v.pipe(v.array(v.pipe(v.string(), v.trim(), v.minLength(1))), v.maxLength(20)));
/* The wire never carries an author: the server stamps it from the connection.
   Order is load-bearing — `v.object` strips unknown keys, so the reply variant
   must match before the target and range variants. */
const CommentInputSchema = v.union([
    v.object({
        parentId: v.string(),
        body: commentBody,
        mentions: commentMentions,
    }),
    v.object({
        target: v.pipe(v.string(), v.minLength(1)),
        body: commentBody,
        mentions: commentMentions,
    }),
    v.object({
        start: v.number(),
        end: v.number(),
        body: commentBody,
        mentions: commentMentions,
    }),
]);
const FieldValueSchema = v.union([
    v.string(),
    v.number(),
    v.boolean(),
    v.null(),
]);
const GraphNodeSchema = v.object({
    id: v.string(),
    type: v.string(),
    name: v.string(),
    position: v.optional(v.object({ x: v.number(), y: v.number() })),
    content: v.optional(v.record(v.string(), v.unknown())),
});
const GraphEdgeSchema = v.object({
    id: v.string(),
    sourceId: v.string(),
    targetId: v.string(),
    sourceHandle: v.optional(v.string()),
    targetHandle: v.optional(v.string()),
});
const GraphNodePatchSchema = v.partial(v.strictObject(v.omit(GraphNodeSchema, ['id']).entries));
const GraphEdgePatchSchema = v.partial(v.strictObject(v.omit(GraphEdgeSchema, ['id']).entries));
/** Mirrors the `FieldOp` union in `./types` for wire-boundary validation. */
const FieldOpSchema = v.union([
    v.object({ kind: v.literal('set'), value: FieldValueSchema }),
    v.object({
        kind: v.literal('add'),
        member: v.string(),
        attrs: v.optional(v.record(v.string(), FieldValueSchema)),
    }),
    v.object({ kind: v.literal('remove'), member: v.string() }),
    v.object({ kind: v.literal('addNode'), node: GraphNodeSchema }),
    v.object({ kind: v.literal('removeNode'), nodeId: v.string() }),
    v.object({
        kind: v.literal('updateNode'),
        nodeId: v.string(),
        patch: GraphNodePatchSchema,
    }),
    v.object({ kind: v.literal('addEdge'), edge: GraphEdgeSchema }),
    v.object({ kind: v.literal('removeEdge'), edgeId: v.string() }),
    v.object({
        kind: v.literal('updateEdge'),
        edgeId: v.string(),
        patch: GraphEdgePatchSchema,
    }),
]);
const CommentControlSchema = v.variant('op', [
    v.object({
        c: v.literal('comment'),
        op: v.literal('add'),
        data: CommentInputSchema,
    }),
    v.object({
        c: v.literal('comment'),
        op: v.literal('reply'),
        data: CommentInputSchema,
    }),
    v.object({
        c: v.literal('comment'),
        op: v.literal('resolve'),
        data: v.string(),
    }),
    v.object({
        c: v.literal('comment'),
        op: v.literal('reopen'),
        data: v.string(),
    }),
]);
const ControlSchema = v.variant('c', [
    v.object({ c: v.literal('sync'), vv: v.string() }),
    /** Server → client reply to `sync` carrying the server's current version vector. */
    v.object({ c: v.literal('sync-reply'), vv: v.string() }),
    CommentControlSchema,
    v.object({
        c: v.literal('suggestion'),
        op: v.picklist(['accept', 'reject']),
        id: v.string(),
        feedback: v.optional(v.string()),
    }),
    v.object({
        c: v.literal('field-suggestion'),
        op: v.picklist(['accept', 'reject']),
        id: v.string(),
        feedback: v.optional(v.string()),
        override: v.optional(FieldOpSchema),
    }),
    v.object({
        c: v.literal('milestone'),
        op: v.picklist(['complete', 'reopen']),
        id: v.string(),
    }),
    v.object({ c: v.literal('history'), op: v.picklist(['undo', 'redo']) }),
    /**
     * Server → client: a write frame was refused because the connection is
     * read-only (or the room only accepts server-side writes). Sent instead of
     * silently dropping, so a client that bypassed its own disabled UI — or a
     * genuine permission change mid-session — is observable rather than a
     * mystery "my edits don't save".
     */
    v.object({ c: v.literal('denied'), reason: v.string() }),
]);
/**
 * Encodes a validated control message object to its JSON wire form.
 *
 * @param msg - Validated control message to encode.
 * @returns The JSON wire representation of `msg`.
 */
export function encodeControl(msg) {
    return JSON.stringify(v.parse(ControlSchema, msg));
}
/**
 * Decodes and validates a JSON control message string.
 *
 * @param text - JSON control frame to parse and validate.
 * @returns The validated control message.
 * @throws If `text` is not valid JSON or does not match the control schema.
 */
export function decodeControl(text) {
    let raw;
    try {
        raw = JSON.parse(text);
    }
    catch {
        throw new Error('invalid control json');
    }
    return v.parse(ControlSchema, raw);
}
//# sourceMappingURL=protocol.js.map